import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import produce from "immer";

import "./Templates.scss";
import { ErrorBoundary, ErrorMessage, Page, PageHeader, Loader } from "components";
import { Button, Modal, ModalHeader, ModalBody, ModalFooter, IconButton} from '@cfa/react-components';
import { AsyncStatus } from "enums";
import { ITemplate } from "interfaces";
import { ManufacturersService, TemplatesService, UpdateFilesService, UpdateTypesService, parseAxiosError } from "services";

interface ITemplatesPageState {
    loadStatus: AsyncStatus;
    loadError?: string;
    deleteStatus?: AsyncStatus;
    deleteError?: string;
    selectedTemplate?: ITemplate;
}

export function Templates() {

    const [pageState, setPageState] = useState<ITemplatesPageState>({
        loadStatus: AsyncStatus.Loading,
        deleteStatus: AsyncStatus.Initial,
    });
    const { loadStatus, loadError, deleteStatus, deleteError, selectedTemplate } = pageState;

    // delete modal control
    const [deleteShow, setDeleteShow] = useState(false);
    const handleDeleteClose = () => setDeleteShow(false);
    const showDeleteModal = (t) => {
        setPageState(
            produce((draft) => {
                draft.selectedTemplate = t;
            })
        );
        setDeleteShow(true);
    };

    // data is preloaded in app.tsx, so just assign from service
    useEffect(() => {
        if (loadStatus === AsyncStatus.Success) return;

        setPageState(
            produce((draft) => {
                draft.loadStatus = AsyncStatus.Success;
            })
        );
    });
 
    // soft deletes the Templates in the DB
    const onDeleteTemplate = (t: ITemplate) => {
        setPageState(
            produce((draft) => {
                draft.selectedTemplate = t;
                draft.deleteError = '';
                draft.deleteStatus = AsyncStatus.Loading;
            })
        )
        TemplatesService.deleteTemplate(t.id)
            .then(() => {
                setPageState(
                    produce((draft) => {
                        draft.selectedTemplate = undefined;
                        draft.deleteStatus = AsyncStatus.Success;
                    })
                )
                setDeleteShow(false);
            })
            .catch((err) => {
                setPageState(
                    produce((draft) => {
                        draft.deleteError = parseAxiosError(err);
                        draft.deleteStatus = AsyncStatus.Error;
                    })
                );
            });
        
    }

    function getModelNames(t: ITemplate) {
        let names = [];

        t.deviceModelIDs.forEach((id) => {
            names.push(ManufacturersService.modelsMap[id].name);
        })

        return names.join(" | ");
    }

    function daysHours(ms) {
        const days = Math.floor(ms / (24*60*60*1000));
        const daysms = ms % (24*60*60*1000);
        const hours = Math.floor(daysms / (60*60*1000));

        if (hours === 0) return days + " days";

        return days + " days, " + hours + " hours";
    }

    return (<>
        <Page>

            <PageHeader title="Templates"/>

            <div className="row templates_colHeaders">
                <div className="templates_colHeader templates_colHeadersMain">
                    Template
                </div>
                <div className="templates_colHeader templates_colHeadersCreatedAt">
                    Update File
                </div>
                <div className="templates_colHeader templates_colHeadersAction"></div>
            </div>
            
            <div className="templates_list">
                <ErrorBoundary>
                    {loadStatus === AsyncStatus.Loading && <Loader />}
                    {loadStatus === AsyncStatus.Success &&
                        TemplatesService.allTemplates.length > 0 && TemplatesService.allTemplates.map((t) => {
                            return (
                                <div className="templates_listItem" key={t.id}>
                                    <div className="templates_listCol templates_listColMain">
                                        <div className="templates_listColSub">
                                            <div><b>Name:</b> {t.name}</div>
                                            <div><b>Description:</b>  {t.description}</div>
                                            <div><b>Notifiers:</b> {t.notifiers.join(" | ")}</div>
                                        </div>
                                        <div className="templates_listColSub">
                                            <div><b>Manufacturer:</b> {ManufacturersService.manufacturersMap[t.manufacturerID].name}</div>
                                            <div><b>Models:</b> {getModelNames(t)}</div>
                                            <div><b>Update Type:</b> {UpdateTypesService.updateTypesMap[t.updateTypeID].description}</div>
                                            <div><b>Duration:</b> {daysHours(t.duration)}</div>
                                        </div>
                                    </div>
                                    <div className="templates_listCol templates_listColUpdateFile">
                                        <div><b>Version:</b> {UpdateFilesService.allUpdateFilesMap[t.updateFileID].version}</div>
                                        <div><b>Description:</b> {UpdateFilesService.allUpdateFilesMap[t.updateFileID].description}</div>
                                    </div>
                                    <div className="templates_listCol templates_listColAction">
                                        <IconButton
                                            size="sm"
                                            onClick={() => showDeleteModal(t)}
                                        >
                                            <FontAwesomeIcon icon="trash" />
                                        </IconButton>
                                    </div>
                                </div>
                            );
                        })}
                    {loadStatus === AsyncStatus.Success &&
                        TemplatesService.allTemplates.length === 0 && (
                            <div className="templates_listEmpty">
                                <FontAwesomeIcon
                                    icon="search"
                                    className="templates_listEmptyIcon"
                                />
                                <div className="templates_listEmptyText">
                                    No templates found.
                                </div>
                            </div>
                        )}
                    {loadStatus === AsyncStatus.Error && (
                        <ErrorMessage message={loadError} />
                    )}
                </ErrorBoundary>
            </div>
        </Page>

        <Modal show={deleteShow} onClose={handleDeleteClose}>
            <ModalHeader>
                Delete Template
            </ModalHeader>
            <ModalBody>
                <p>Are you sure you want to delete this template?</p>
                <div className="templates_deleteHeader">
                    Name: {selectedTemplate && selectedTemplate.name}<br/>
                    ID: {selectedTemplate && selectedTemplate.id}
                </div>
            </ModalBody>
            <ModalFooter>
                {deleteStatus === AsyncStatus.Error && (
                    <ErrorMessage message={deleteError} />
                )}
                <Button variant="outlined" disabled={deleteStatus === AsyncStatus.Loading} onClick={handleDeleteClose}>
                    Cancel
                </Button>
                <Button loading={deleteStatus === AsyncStatus.Loading} onClick={() => onDeleteTemplate(selectedTemplate)}>
                    Delete
                </Button>
            </ModalFooter>
        </Modal>
    </>);
}
